import React, {useEffect, useState} from 'react';
import Container from "@mui/material/Container";
import {useNavigate, useParams} from "react-router";
import ProductsManager from "../../../managers/productsManager/productsManager";
import {Trans, useTranslation} from "react-i18next";
import Grid from '@mui/material/Grid2';
import Gallery from "./gallery/Gallery";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CartManager from "../../../managers/cartManager/cartManager";
import CartItemContract from "../../../managers/cartManager/contracts/cartItemContract";
import ParamTypes from "../../../productParamHandler/paramTypes";
import CustomMarkdown from "../../../components/customMarkdown/CustomMarkdown";
import {useProductPromo} from "../../../managers/productPromoManager/productPromoManager";
import {grey} from "@mui/material/colors";
import ListItemText from "@mui/material/ListItemText";
import OnSaleLine from "../onSaleLine/OnSaleLine";
import UserManager from "../../../managers/userManager/userManager";
import IconButton from "@mui/material/IconButton";
import {EditOutlined} from "@mui/icons-material";
import {logGAEvent} from "../../../firebaseConfig/firebaseConfig";


function ProductPage(props) {
    const navigate = useNavigate();
    const {id} = useParams();
    const [params, setParams] = useState({});
    const [product, setProduct] = useState();
    const [selectedImageIndex, setSelectedImageIndex] = useState();
    const productPromo = useProductPromo(id);
    const {t} = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            const data = await ProductsManager.getProduct(id);
            if (data.id) {
                logGAEvent('view_item', {id: data.id});
                setProduct(data);
                document.title = `${t(data.name)} ${t('title.dtf')}`;
            } else {
                navigate('/404');
            }
        };

        fetchData();
    }, [id]);

    const getPrice = (promo) => {
        let price = Number(product?.price || 0)

        if (product?.params) {
            const paramPrice = product.params.map(param => params?.[param.key] || param.values?.[0]).filter(paramValue => paramValue?.price)[0];
            if (paramPrice) {
                return Number(paramPrice.price);
            }

            product.params.map(param => ({
                value: params?.[param.key] || param.values?.[0],
                param: param
            })).filter(obj => obj.value?.priceModifier && obj.value?.priceModifier > 0).forEach(obj => {
                price = Number(ParamTypes.getPriceModifier(obj.param, obj.value, price));
            })
        }

        if (promo) {
            return (price * (1 - promo)).toFixed(2);
        }

        return price.toFixed(2);
    }

    const changeCiProps = (key, value) => {
        if (value.goToImgIndex) {
            setSelectedImageIndex(Number(value.goToImgIndex));
        }

        const newCiProps = Object.assign({}, params);
        newCiProps[key] = value;
        setParams(newCiProps);
    }

    const addToCart = event => {
        if (product) {
            logGAEvent('add_to_cart', product);
            const decoratedParams = Object.assign({}, params);
            if (product.params) {
                product.params.forEach(param => {
                    if (!decoratedParams[param.key]) {
                        decoratedParams[param.key] = param.values[0];
                    }
                });
            }

            CartManager.addItemToCart(new CartItemContract(product.id, 1, decoratedParams));
            event.stopPropagation();
        }
    }

    const handleBack = () => {
        navigate('/products');
    }

    const handleEdit = () => {
        navigate(`/admin/products/${id}`);
    }

    return (product &&
        <Container maxWidth="md">
            <Button sx={{mt: 2}} color={'secondary'} onClick={handleBack}>&lt; <Trans
                i18nKey={'buttons.back'}/></Button>
            {UserManager.isAdmin() ? <IconButton sx={{mt: 2}} color={'secondary'} onClick={handleEdit}>
                <EditOutlined fontSize="small" sx={{color: 'inherit'}}/>
            </IconButton> : ''}
            {Object.keys(productPromo).length ? <OnSaleLine productPromo={productPromo}/> : ''}

            <Grid container mt={{xs: 2, md: 5}} mb={3}>
                <Grid size={{xs: 12, md: 9, lg: 9}} sx={{pr: {xs: 0, md: 3}}}>
                    <Gallery id={id} images={product.images || []} selectedImageIndex={selectedImageIndex}/>
                </Grid>
                <Grid size={{xs: 12, md: 3, lg: 3}}
                      sx={{
                          textAlign: {
                              sm: 'left',
                              md: 'right'
                          }
                      }}
                >
                    <Grid size={12}
                          container
                          flexDirection={{sm: 'row', md: 'column'}}
                          alignItems={{xs: 'center', sm: 'center', md: 'initial'}}
                          justifyContent={{xs: 'space-between', sm: 'space-between', md: 'initial'}}
                    >
                        <Grid>
                            <Typography variant="h4" gutterBottom textAlign={{sm: 'left', md: 'right'}}>
                                <Trans i18nKey={product.name}/>
                            </Typography>
                        </Grid>
                        <Grid>
                            {productPromo['1'] ?
                                <ListItemText
                                    primary={<Typography variant="h6" gutterBottom textAlign={'right'}>
                                        {getPrice(productPromo['1'])} Lei
                                    </Typography>}
                                    secondary={<Typography variant="body1" sx={{
                                        fontWeight: 'medium',
                                        textDecoration: 'line-through',
                                        color: grey[400]
                                    }} textAlign={'right'}>
                                        {getPrice()} Lei
                                    </Typography>}
                                /> :
                                <Typography variant="h6" gutterBottom textAlign={'right'}>
                                    {getPrice()} Lei
                                </Typography>}
                        </Grid>
                    </Grid>

                    <Grid size={12}>
                        <Button variant="contained" color="secondary" size="large" onClick={addToCart}>
                            <Trans i18nKey={'productPage.addToCart'}/>
                        </Button>
                    </Grid>

                    {product.params ?
                        <Grid container gap={2} pt={2}>
                            {product.params.map(param => <Grid key={param.key} size={12}>
                                {ParamTypes.GET_COMPONENT(param, changeCiProps, params?.[param.key] || param.values?.[0], false)}
                            </Grid>)}
                        </Grid> : ''}
                </Grid>
            </Grid>

            <Grid container size={12}>
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom>
                        <Trans i18nKey={'productPage.description'}/>
                    </Typography>
                </Grid>
                <Grid>
                    <CustomMarkdown children={[product.description]}/>
                </Grid>
            </Grid>
        </Container>
    );
}

export default ProductPage;
